import { computeSnap, type SnapGuides, type SnapTarget } from '@/lib/layers/computeSnap';

/** Snap a Cortes headline center-anchor using the same composition guides
 *  as media-layer move (edges + midlines). Width/height are the on-canvas
 *  box fractions so a full-width Text Bar can disable X by passing
 *  thresholdX: 0.
 *
 *  After the layer snap, the box is kept fully on-canvas so dragging past
 *  an edge latches flush (and lights that guide) instead of hanging off. */
export function snapHeadlineAnchor({
  anchor,
  width,
  height,
  thresholdX,
  thresholdY,
}: {
  anchor: { x: number; y: number };
  width: number;
  height: number;
  thresholdX: number;
  thresholdY: number;
}): { x: number; y: number; guides: SnapGuides } {
  const snapped = computeSnap({
    rect: {
      x: anchor.x - width / 2,
      y: anchor.y - height / 2,
      w: width,
      h: height,
    },
    thresholdX,
    thresholdY,
  });
  const { rect, x: gx, y: gy } = keepBoxOnCanvas(snapped.rect, snapped.guides, thresholdX, thresholdY);
  return {
    x: rect.x + rect.w / 2,
    y: rect.y + rect.h / 2,
    guides: { x: gx, y: gy },
  };
}

function keepBoxOnCanvas(
  rect: { x: number; y: number; w: number; h: number },
  guides: SnapGuides,
  thresholdX: number,
  thresholdY: number,
): { rect: { x: number; y: number; w: number; h: number }; x: SnapTarget | null; y: SnapTarget | null } {
  let { x, y } = rect;
  const { w, h } = rect;
  let gx = guides.x;
  let gy = guides.y;
  if (x < 0) {
    x = 0;
    if (thresholdX > 0) gx = 'start';
  } else if (x + w > 1) {
    x = 1 - w;
    if (thresholdX > 0) gx = 'end';
  }
  if (y < 0) {
    y = 0;
    if (thresholdY > 0) gy = 'start';
  } else if (y + h > 1) {
    y = 1 - h;
    if (thresholdY > 0) gy = 'end';
  }
  return { rect: { x, y, w, h }, x: gx, y: gy };
}
